/***************************************************************************************
 * FileName      : EquipmentDataService.ts
 * Description   : 장비 JSON 데이터 로드 서비스 (구성요소 부품 목록, 모니터링 센서 데이터 포함)
 *                  Matterport 연동 정보(Sweep 등)는 assets/json/matterport/*.json에서 별도 로드
 *                  enum/type re-export 포함 (DeviceCategory, ControlType, SensorType, DataValueType, EquipmentType)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.10
 * Modified Date : 2026.02.11
 * Reference     : packages/scjeil-smart_ict_lab-app/assets/json/equipment/*.json, packages/scjeil-smart_ict_lab-app/assets/json/matterport/*.json, packages/scjeil-smart_ict_lab-app/src/types/
 ***************************************************************************************/

// 2026.02.10 : 박은정 : 장비 JSON 파일에서 데이터 로드
// 2026.02.11 : 박은정 : assets/json/equipment/ 경로로 이동
import circulationData from '../../assets/json/equipment/circulation.json';
import distillationData from '../../assets/json/equipment/distillation.json';
import heatExchangerData from '../../assets/json/equipment/heat-exchanger.json';
// 2026.02.11 : 박은정 : Matterport 연동 설정 JSON에서 Sweep 정보 로드 (equipment JSON에서 분리)
import circulationMatterport from '../../assets/json/matterport/circulation.json';
import distillationMatterport from '../../assets/json/matterport/distillation.json';
import heatExchangerMatterport from '../../assets/json/matterport/heat-exchanger.json';
// 2026.02.10 : 박은정 : 장비 관련 enum/type import 및 re-export (파일별 분리 → 소비자 편의 re-export)
import { DeviceCategory } from '../types/DeviceCategory';
import { ControlTypes, type ControlType } from '../types/ControlType';
import { SensorTypes, type SensorType, SENSOR_TYPE_HEADINGS, SENSOR_TYPE_ORDER } from '../types/SensorType';
import { DataValueTypes, type DataValueType } from '../types/DataValueType';
import { EquipmentTypes, type EquipmentType } from '../types/EquipmentType';

// 2026.02.10 : 박은정 : 소비자(컴포넌트)가 EquipmentDataService에서 enum/type을 함께 import할 수 있도록 re-export
export { DeviceCategory };
export { ControlTypes, type ControlType };
export { SensorTypes, type SensorType, SENSOR_TYPE_HEADINGS, SENSOR_TYPE_ORDER };
export { DataValueTypes, type DataValueType };
export { EquipmentTypes, type EquipmentType };

// 2026.02.10 : 박은정 : Sweep 위치 좌표 인터페이스
export interface SweepPosition {
  x: number;
  y: number;
  z: number;
}

// 2026.02.10 : 박은정 : 장비별 Sweep 정보 인터페이스
export interface EquipmentSweep {
  sweepId: string;
  position: SweepPosition;
}

// 2026.02.10 : 박은정 : 제어 포인트 인터페이스 (controls 배열 항목)
export interface ControlData {
  controlId: string;
  name: string;
  controlType: ControlType;
  dataType: DataValueType;
  unit?: string;
}

// 2026.02.10 : 박은정 : 센서 포인트 인터페이스 (sensors 배열 항목)
export interface SensorData {
  sensorId: string;
  name: string;
  sensorType: SensorType;
  dataType: DataValueType;
  unit?: string;
}

// 2026.02.10 : 박은정 : 장비 디바이스(부품) 인터페이스
export interface DeviceData {
  deviceId: string;
  name: string;
  category: DeviceCategory;
  role?: string;
  detail?: string;
  controls?: ControlData[];
  sensors?: SensorData[];
}

// 2026.02.10 : 박은정 : 구성요소 부품 목록 항목 (LearningPanel 구성요소 카드용)
// 2026.02.20 : 박은정 : deviceId 필드 추가 (핫스팟 태그 연동을 위한 디바이스 식별자)
export interface ComponentPart {
  deviceId: string; // 예: "PP-102"
  name: string;     // 예: "PP-102 (마그네틱 펌프)"
  role: string;     // 예: "칠러부 냉수 순환"
  detail: string;   // 예: "인버터 0~60Hz"
}

// 2026.02.10 : 박은정 : 모니터링 데이터 행 (센서 1개 = 1행)
export interface MonitoringDataRow {
  label: string;  // 예: "TIT-101"
  value: string;  // 예: "-" (실시간 연동 전 기본값)
  unit: string;   // 예: "℃"
  desc: string;   // 예: "온수 입구"
}

// 2026.02.10 : 박은정 : 모니터링 섹션 (sensorType별 그룹)
export interface MonitoringSection {
  heading: string;          // 예: "온도 (Temperature)"
  dataRows: MonitoringDataRow[];
}

// 2026.02.11 : 박은정 : 모니터 배치 설정 인터페이스 (Matterport 공간 내 모니터 좌표, 색상, 이미지)
export interface MatterportMonitor {
  monitorId: string;
  name: string;
  color: string;
  defaultImageUrl: string;
  corners: {
    topLeft: SweepPosition;
    topRight: SweepPosition;
    bottomLeft: SweepPosition;
    bottomRight: SweepPosition;
  };
}

// 2026.02.11 : 박은정 : 뷰포인트 프리셋 카메라 회전 인터페이스 (pitch / yaw, 도 단위)
export interface ViewpointRotation {
  /** Pitch (상하 방향, 도 단위) */
  x: number;
  /** Yaw (좌우 방향, 도 단위) */
  y: number;
}

// 2026.02.11 : 박은정 : 뷰포인트 프리셋 인터페이스 (장비별 카메라 방향 + 줌 설정)
export interface ViewpointPreset {
  /** 카메라 회전 (pitch / yaw, 도 단위) */
  rotation: ViewpointRotation;
  /** 줌 레벨 (1.0 = 100%) */
  zoom: number;
}

// 2026.02.11 : 박은정 : Matterport 연동 설정 인터페이스 (Sweep, 뷰포인트 프리셋, 모니터 배치 등)
export interface MatterportData {
  equipmentId: string;
  modelId: string;
  sweep: EquipmentSweep;
  viewpoint?: ViewpointPreset;
  monitor: MatterportMonitor;
}

// 2026.02.10 : 박은정 : 장비 JSON 데이터 최상위 인터페이스
// 2026.02.11 : 박은정 : sweep 필드를 assets/json/matterport/*.json으로 분리
export interface EquipmentData {
  equipmentId: string;
  name: string;
  type: EquipmentType;
  process: string;
  location: string;
  devices: DeviceData[];
}

// 2026.02.10 : 박은정 : 장비 JSON 데이터 일괄 로드 (equipmentId 기준 Map)
const equipmentMap: Record<string, EquipmentData> = {};

// 2026.02.10 : 박은정 : 각 장비 JSON 파일을 equipmentId 키로 등록
[circulationData, distillationData, heatExchangerData].forEach((data) => {
  const equipment = data as unknown as EquipmentData;
  equipmentMap[equipment.equipmentId] = equipment;
});

// 2026.02.11 : 박은정 : Matterport 연동 설정 일괄 로드 (equipmentId 기준 Map)
const matterportMap: Record<string, MatterportData> = {};

// 2026.02.11 : 박은정 : 각 Matterport JSON 파일을 equipmentId 키로 등록
[circulationMatterport, distillationMatterport, heatExchangerMatterport].forEach((data) => {
  const mp = data as unknown as MatterportData;
  matterportMap[mp.equipmentId] = mp;
});

/**
 * 장비 데이터 서비스
 * - JSON 파일에서 로드한 장비 정보를 제공
 * - Sweep ID 및 좌표 조회 기능 포함
 */
export class EquipmentDataService {
  private static instance: EquipmentDataService;

  // 2026.02.10 : 박은정 : 싱글톤 패턴
  static getInstance(): EquipmentDataService {
    if (!EquipmentDataService.instance) {
      EquipmentDataService.instance = new EquipmentDataService();
    }
    return EquipmentDataService.instance;
  }

  // 2026.02.10 : 박은정 : 장비 ID로 Sweep ID 조회
  // 2026.02.11 : 박은정 : Matterport JSON에서 조회하도록 변경
  getSweepId(equipmentId: string): string | null {
    const mp = matterportMap[equipmentId];
    return mp?.sweep?.sweepId || null;
  }

  // 2026.02.10 : 박은정 : 장비 ID로 Sweep 위치 좌표 조회
  // 2026.02.11 : 박은정 : Matterport JSON에서 조회하도록 변경
  getSweepPosition(equipmentId: string): SweepPosition | null {
    const mp = matterportMap[equipmentId];
    return mp?.sweep?.position || null;
  }

  // 2026.02.10 : 박은정 : 장비 ID로 Sweep 전체 정보 조회
  // 2026.02.11 : 박은정 : Matterport JSON에서 조회하도록 변경
  getSweep(equipmentId: string): EquipmentSweep | null {
    const mp = matterportMap[equipmentId];
    return mp?.sweep || null;
  }

  // 2026.02.10 : 박은정 : 전체 장비 Sweep 맵 반환 (equipmentId → sweepId)
  // 2026.02.11 : 박은정 : Matterport JSON에서 조회하도록 변경
  getAllSweepMap(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const [id, mp] of Object.entries(matterportMap)) {
      if (mp.sweep?.sweepId) {
        map[id] = mp.sweep.sweepId;
      }
    }
    return map;
  }

  // 2026.02.11 : 박은정 : 장비 ID로 Matterport 연동 설정 전체 조회
  getMatterportData(equipmentId: string): MatterportData | null {
    return matterportMap[equipmentId] || null;
  }

  // 2026.02.11 : 박은정 : 장비 ID로 뷰포인트 프리셋 조회
  getViewpointPreset(equipmentId: string): ViewpointPreset | null {
    const mp = matterportMap[equipmentId];
    return mp?.viewpoint || null;
  }

  // 2026.02.10 : 박은정 : 장비 전체 데이터 조회
  getEquipmentData(equipmentId: string): EquipmentData | null {
    return equipmentMap[equipmentId] || null;
  }

  // 2026.02.10 : 박은정 : 전체 장비 ID 목록 조회
  getAllEquipmentIds(): string[] {
    return Object.keys(equipmentMap);
  }

  // 2026.02.10 : 박은정 : 장비 구성요소 부품 목록 조회 (role 필드가 있는 디바이스만 반환)
  getComponentParts(equipmentId: string): ComponentPart[] {
    const equipment = equipmentMap[equipmentId];
    if (!equipment) return [];

    // 2026.02.20 : 박은정 : deviceId 필드 추가 (핫스팟 태그 연동용)
    return equipment.devices
      .filter((device) => device.role)
      .map((device) => ({
        deviceId: device.deviceId,
        name: `${device.deviceId} (${device.name})`,
        role: device.role!,
        detail: device.detail || '',
      }));
  }

  // 2026.02.11 : 박은정 : 제어 장비(Control 카테고리) 목록 조회 (좌측 패널 장비 상태 표시용)
  getControlDevices(equipmentId: string): DeviceData[] {
    const equipment = equipmentMap[equipmentId];
    if (!equipment) return [];
    return equipment.devices.filter((device) => device.category === DeviceCategory.Control);
  }

  // 2026.02.11 : 박은정 : 장비 전체 디바이스 목록 조회
  getDevices(equipmentId: string): DeviceData[] {
    const equipment = equipmentMap[equipmentId];
    if (!equipment) return [];
    return equipment.devices;
  }

  // 2026.02.10 : 박은정 : 모니터링 센서 데이터 조회 (sensorType별 그룹화하여 반환)
  getMonitoringData(equipmentId: string): MonitoringSection[] {
    const equipment = equipmentMap[equipmentId];
    if (!equipment) return [];

    // 2026.02.10 : 박은정 : 센서가 있는 디바이스를 sensorType별로 그룹화
    const grouped: Record<string, MonitoringDataRow[]> = {};

    for (const device of equipment.devices) {
      if (!device.sensors || device.sensors.length === 0) continue;

      for (const sensor of device.sensors) {
        const sType = sensor.sensorType || 'Other';
        if (!grouped[sType]) {
          grouped[sType] = [];
        }
        grouped[sType].push({
          label: sensor.sensorId, // 2026.02.11 : 박은정 : 센서 ID 사용 (시나리오 JSON의 센서 ID와 매칭)
          value: '-',
          unit: sensor.unit || '',
          desc: device.detail || device.name,
        });
      }
    }

    // 2026.02.10 : 박은정 : 정의된 순서대로 섹션 배열 생성 (SENSOR_TYPE_ORDER, SENSOR_TYPE_HEADINGS 사용)
    const sections: MonitoringSection[] = [];
    for (const sType of SENSOR_TYPE_ORDER) {
      if (grouped[sType]) {
        sections.push({
          heading: SENSOR_TYPE_HEADINGS[sType] || sType,
          dataRows: grouped[sType],
        });
        delete grouped[sType];
      }
    }
    // 정의되지 않은 sensorType이 있으면 마지막에 추가
    for (const [sType, rows] of Object.entries(grouped)) {
      sections.push({
        heading: SENSOR_TYPE_HEADINGS[sType] || sType,
        dataRows: rows,
      });
    }

    return sections;
  }
}

